import React from 'react';
import styles from './my-content.module.scss';
import classNames from 'classnames';
import { ScreenTitle, Button, SearchBox, ServerSideTable } from 'src/components/design-system';
import { MyContentProvider, useMyContentContext } from './my-content.context';
import { TableStats, ViewsModal } from './components';
import { Dropdown, notification, Spin } from 'antd';
import {
  KnowledgeDeletePopup,
  KnowledgeOwnerPopup,
  RenderBinderSelector,
} from 'src/components/knowledge-content/knowledge-content-editor';
import {
  getRoot,
  getCategoryById,
  getCategoriesById,
  generateBreadcrumbs,
} from 'shared/store/selectors/knowledge.selector';
import { STORE_ENTITY_TYPES, MS_PER_DAY } from 'shared/constants';
import { useMyContentAnalytics } from './hooks/useMyContentAnalytics';
import { useMyContentActions } from './hooks/useMyContentActions';
import { useColumns } from './hooks/use-columns';
import { EVENTS } from 'shared/constants/analytics';
import { GenericErrorPage } from 'src/components/errors/error-page.component';
import { formatNumber } from 'shared/utils';
import { useSelector } from 'react-redux';
import { isSyncedContentItem } from './my-content.utils';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Tooltip } from 'src/components/core';

function MyContentPageContent() {
  const {
    lang,
    contentItems,
    pageLoading,
    searchLoading,
    actionLoading,
    isSearchStale,
    error,
    totalItems,
    expiredCount,
    categoriesWithItems,
    filters,
    sortBy,
    sortOrder,
    params,
    updateFilter,
    resetFilters,
    extendItemExpiration,
    shareItemToHub,
    removeContentItem,
    moveItemToCategory,
    updateItemOwnership,
    triggerContentUpdate,
    handleFilterChange,
    serverSideTableRef,
    fetchData,
    handleDataLoad,
    handleDataError,
    selectedRowKeys,
    clearSelection,
    handleSelectionChange,
  } = useMyContentContext();

  const [api, contextHolder] = notification.useNotification();
  const [deleteConfirmation, setDeleteConfirmation] = React.useState(null);
  const categoriesById = useSelector(({ knowledge }) => knowledge.categoriesById);
  const [isMoveModalOpen, setIsMoveModalOpen] = React.useState(false);
  const [moveTarget, setMoveTarget] = React.useState(null);
  const [ownershipModalEntity, setOwnershipModalEntity] = React.useState(null);
  const [viewsModalItem, setViewsModalItem] = React.useState(null);

  const searchInputRef = React.useRef(null);

  // Analytics hook
  const { trackActionEvent, handleItemClick, handleOwnerClick, handlePathClick } =
    useMyContentAnalytics({
      filters,
      contentItems,
      totalItems,
      expiredCount,
      pageLoading,
      searchLoading,
      selectedRowKeys,
    });

  const handleMoveModalClose = React.useCallback(() => {
    setIsMoveModalOpen(false);
    setMoveTarget(null);
  }, []);

  // Generic bulk action handler
  const handleBulkAction = async (
    actionFn,
    successMsg,
    failedMsg,
    successDescMsg,
    failedDescMsg,
    event,
    additionalParams = {},
  ) => {
    if (selectedRowKeys.length === 0) return;

    const results = await Promise.all(selectedRowKeys.map(actionFn));

    // Check for any errors
    const errors = results.filter(r => r && r.error);
    const successes = results.filter(r => r && r.success);

    if (errors.length > 0) {
      api.error({
        title: failedMsg,
        description: failedDescMsg
          .replace('{{errors}}', errors.length)
          .replace('{{successes}}', successes.length),
        placement: 'bottomRight',
      });
    } else {
      api.success({
        title: successMsg,
        description: successDescMsg.replace('{{count}}', successes.length),
        placement: 'bottomRight',
      });
      trackActionEvent(event, { ...additionalParams, itemCount: successes.length }, true);
    }

    // Clear selection after update
    clearSelection();

    // Trigger knowledge update once for the bulk operation
    triggerContentUpdate();
  };

  // Actions hook
  const {
    handleBulkExtend,
    handleBulkShare,
    handleBulkMoveTo,
    createBulkMoveHandler,
    handleBulkOwnership,
    handleBulkDelete,
    handleBulkDeleteConfirm,
    handleBulkOwnershipUpdate,
  } = useMyContentActions({
    selectedRowKeys,
    contentItems,
    trackActionEvent,
    handleBulkAction,
    extendItemExpiration,
    shareItemToHub,
    moveItemToCategory,
    updateItemOwnership,
    removeContentItem,
    setIsMoveModalOpen,
    setMoveTarget,
    setOwnershipModalEntity,
    setDeleteConfirmation,
    handleMoveModalClose,
    lang,
  });

  /**
   * Handles the delete confirmation popup dismiss.
   */
  const handleDeleteDismiss = () => {
    setDeleteConfirmation(null);
  };

  /**
   * Handles the delete confirmation and executes the deletion.
   */
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;
    await handleBulkDeleteConfirm(deleteConfirmation);
    setDeleteConfirmation(null);
  };

  /**
   * Handles move target selection.
   */
  const handleMoveTargetSelect = target => {
    setMoveTarget(target);
  };

  /**
   * Handles bulk move operation using the current moveTarget state.
   */
  const handleBulkMove = React.useCallback(() => {
    if (moveTarget) {
      const bulkMoveHandler = createBulkMoveHandler(moveTarget);
      bulkMoveHandler();
    }
  }, [moveTarget, createBulkMoveHandler]);

  /**
   * Handles the ownership modal close.
   */
  const handleOwnershipModalClose = () => {
    setOwnershipModalEntity(null);
  };

  /**
   * Handles individual item expiration extension with notifications.
   *
   * @param {string} itemId - ID of the item to extend
   * @param {string} newDate - New expiration date in ISO format
   * @returns {Promise<void>}
   */
  const handleItemExpirationExtension = React.useCallback(
    async (itemId, newDate) => {
      const item = contentItems.find(i => i.id === itemId);
      const originalDate = item?.expiresAt;
      const extensionDays = originalDate
        ? Math.ceil((new Date(newDate) - new Date(originalDate)) / MS_PER_DAY)
        : 0;
      const extensionYears = extensionDays > 0 ? parseFloat((extensionDays / 365).toFixed(2)) : 0;

      const result = await extendItemExpiration(itemId, newDate);

      // Track analytics
      trackActionEvent(
        EVENTS.MY_CONTENT.EXTEND_EXPIRATION,
        {
          itemId,
          extendByDays: extensionDays,
          extendByYears: extensionYears,
          newExpirationDate: newDate,
          success: !result.error,
        },
        false,
      );

      if (result.error) {
        api.error({
          title: lang.EXTENSION_FAILED,
          description: result.error,
          placement: 'bottomRight',
        });
      } else {
        api.success({
          title: lang.EXTENSION_SUCCESSFUL,
          description: lang.ITEM_EXPIRATION_EXTENDED,
          placement: 'bottomRight',
        });
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [extendItemExpiration, lang, contentItems, trackActionEvent],
  );

  // Use columns hook
  const { columns, filterValues, staticFilters } = useColumns({
    lang,
    filters,
    sortBy,
    sortOrder,
    categoriesWithItems,
    handleItemClick,
    handleOwnerClick,
    handlePathClick,
    handleItemExpirationExtension,
    setViewsModalItem,
  });

  const hasSyncedSelection = React.useMemo(
    () =>
      selectedRowKeys.some(rowId =>
        isSyncedContentItem(
          contentItems.find(({ id }) => id === rowId),
          categoriesById,
        ),
      ),
    [selectedRowKeys, contentItems, categoriesById],
  );

  const actionTooltipLang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));

  const blockedLabel = React.useCallback(
    (label, title = actionTooltipLang.READ_ONLY_SYNCED) => (
      <Tooltip title={title} placement="left">
        <span className={styles.blockedBulkAction}>{label}</span>
      </Tooltip>
    ),
    [actionTooltipLang],
  );

  // Every selected row never expires, so "extend by" has no date to move. Blocking it before
  // the click keeps the bulk menu saying the same thing the row already does.
  const hasNoExtendableSelection = React.useMemo(
    () =>
      selectedRowKeys.length > 0 &&
      !selectedRowKeys.some(rowId => contentItems.find(({ id }) => id === rowId)?.expiresAt),
    [selectedRowKeys, contentItems],
  );

  const isExtendBlocked = hasSyncedSelection || hasNoExtendableSelection;

  const bulkActionItems = React.useMemo(
    () => [
      {
        key: 'extend',
        label: isExtendBlocked
          ? blockedLabel(
              lang.EXTEND_BY,
              hasSyncedSelection
                ? actionTooltipLang.READ_ONLY_SYNCED
                : actionTooltipLang.NO_REVIEW_DATE_TO_EXTEND,
            )
          : lang.EXTEND_BY,
        // No submenu when blocked — there is nothing to choose.
        ...(isExtendBlocked
          ? {}
          : {
              children: [
                {
                  key: 'extend-6m',
                  label: lang.SIX_MONTHS,
                  onClick: () => handleBulkExtend(0.5),
                },
                {
                  key: 'extend-1y',
                  label: lang.ONE_YEAR,
                  onClick: () => handleBulkExtend(1),
                },
                {
                  key: 'extend-2y',
                  label: lang.TWO_YEARS,
                  onClick: () => handleBulkExtend(2),
                },
              ],
            }),
      },
      {
        key: 'share',
        label: lang.SHARE_TO_KNOWLEDGE_NETWORK,
        onClick: handleBulkShare,
      },
      {
        key: 'move',
        label: hasSyncedSelection ? blockedLabel(lang.MOVE_TO) : lang.MOVE_TO,
        onClick: hasSyncedSelection ? undefined : handleBulkMoveTo,
      },
      {
        key: 'ownership',
        label: hasSyncedSelection ? blockedLabel(lang.OWNERSHIP_SETTINGS) : lang.OWNERSHIP_SETTINGS,
        onClick: hasSyncedSelection ? undefined : handleBulkOwnership,
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: hasSyncedSelection ? blockedLabel(lang.DELETE) : lang.DELETE,
        onClick: hasSyncedSelection ? undefined : handleBulkDelete,
        danger: !hasSyncedSelection,
      },
    ],
    [
      lang,
      actionTooltipLang,
      hasSyncedSelection,
      isExtendBlocked,
      blockedLabel,
      handleBulkExtend,
      handleBulkShare,
      handleBulkMoveTo,
      handleBulkOwnership,
      handleBulkDelete,
    ],
  );

  if (error) {
    return <GenericErrorPage />;
  }

  return (
    <Spin spinning={actionLoading} tip={lang.PROCESSING}>
      {contextHolder}
      <div className={classNames('new-design-system', styles.root)}>
        <div className={styles.header}>
          <ScreenTitle title={lang.HEADER} containerStyle={styles.pageTitle} />

          <Dropdown
            menu={{ items: bulkActionItems }}
            placement="bottomLeft"
            // Until the knowledge tree loads, hasSyncedSelection can't vet synced rows.
            disabled={selectedRowKeys.length === 0 || !categoriesById}
            trigger={['click']}>
            <Button type="primary" variant="outlined" size="large">
              {selectedRowKeys.length === 0
                ? lang.BULK_ACTIONS
                : lang.ACTIONS_BUTTON.replace('{{count}}', formatNumber(selectedRowKeys.length))}
            </Button>
          </Dropdown>
          <div className={styles.headerActions}>
            <SearchBox
              ref={searchInputRef}
              size="large"
              placeholder={lang.SEARCH_PLACEHOLDER}
              defaultValue={filters.searchText}
              onSearch={value => updateFilter({ searchText: value, page: 1 })}
            />
          </div>
        </div>
        <div className={styles.content}>
          <TableStats />
          <ServerSideTable
            ref={serverSideTableRef}
            fetchData={fetchData}
            params={params}
            onDataLoad={handleDataLoad}
            onDataError={handleDataError}
            filters={filters}
            sortBy={sortBy}
            sortOrder={sortOrder}
            updateParams={updateFilter}
            excludeFilters={['searchText']}
            columnDefs={columns}
            getRowId={p => p.data.id}
            onSelectionChange={handleSelectionChange}
            isStale={isSearchStale || searchLoading || pageLoading}
            containerProps={{ className: styles.table }}
            enableColumnChooser
            enableActiveFilters
            activeFiltersProps={{
              filterValues,
              onFilterChange: handleFilterChange,
              onClearAll: resetFilters,
              staticFilters,
            }}
            themeOverrides={{
              rowHeight: 70,
              headerHeight: 60,
              paginationPanelHeight: 44,
            }}
          />
        </div>
      </div>

      <KnowledgeDeletePopup
        open={!!deleteConfirmation}
        entity={deleteConfirmation}
        onFinish={handleDeleteDismiss}
        onDelete={handleDeleteConfirm}
        onDismiss={handleDeleteDismiss}
      />

      <RenderBinderSelector
        isOpen={isMoveModalOpen}
        onClose={handleMoveModalClose}
        title={lang.MOVE_TITLE}
        subtitle={lang.MOVE_SUBTITLE}
        submitButtonText={lang.MOVE_TO}
        selectTarget={handleMoveTargetSelect}
        onSubmit={handleBulkMove}
        entity={STORE_ENTITY_TYPES.KNOWLEDGE}
        generateBreadcrumbs={generateBreadcrumbs}
        getCategoriesById={getCategoriesById}
        getCategoryById={getCategoryById}
        getRoot={getRoot}
      />

      <KnowledgeOwnerPopup
        open={!!ownershipModalEntity}
        entity={ownershipModalEntity}
        onFinish={handleOwnershipModalClose}
        onUpdate={handleBulkOwnershipUpdate}
        onDismiss={handleOwnershipModalClose}
        onBulkUpdate={handleBulkOwnershipUpdate}
      />

      <ViewsModal
        open={!!viewsModalItem}
        item={viewsModalItem?.item}
        period={viewsModalItem?.period}
        onClose={() => setViewsModalItem(null)}
      />
    </Spin>
  );
}

export function MyContentPage() {
  return (
    <MyContentProvider>
      <MyContentPageContent />
    </MyContentProvider>
  );
}
